import { useEffect, useMemo, useState } from "react";

import { useAuth } from "../../contexts/AuthContext";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import useIntegrations from "../../data/useIntegrations";
import { IntegrationService } from "../../models/integration/Integration";
import date from "date-and-time";
import { useUserLookup } from "../../hooks/useUserLookup";
import LoadingWheel from './../../components/shared/LoadingWheel';
import ShopifyAuthModal from "../../components/adminSettings/authConnections/ShopifyAuthModal";

export default function AuthConnections() {
  const { customerId, userId } = useAuth();
  const { setInfo } = usePageNameContext();
  const { integrations } = useIntegrations();
  const { isLoading, findUser } = useUserLookup();
  const [ shopifyOpen, setShopifyOpen ] = useState(false);

  const tellerooBaseUrl = process.env.REACT_APP_TELLEROO_URL;
  const tellerooClientId = process.env.REACT_APP_TELLEROO_CLIENT_ID;

  const bettyBaseUrl = process.env.REACT_APP_WILMA_SSO_API_URL;
  const ebayBaseUrl = process.env.REACT_APP_EBAY_URL;
  const ebayClientId = process.env.REACT_APP_EBAY_CLIENT_ID;
  const ebayRuName = process.env.REACT_APP_EBAY_RUNAME;
  const scope = "https://api.ebay.com/oauth/api_scope https://api.ebay.com/oauth/api_scope/sell.marketing.readonly https://api.ebay.com/oauth/api_scope/sell.marketing https://api.ebay.com/oauth/api_scope/sell.inventory.readonly https://api.ebay.com/oauth/api_scope/sell.inventory https://api.ebay.com/oauth/api_scope/sell.account.readonly https://api.ebay.com/oauth/api_scope/sell.account https://api.ebay.com/oauth/api_scope/sell.fulfillment.readonly https://api.ebay.com/oauth/api_scope/sell.fulfillment https://api.ebay.com/oauth/api_scope/sell.analytics.readonly https://api.ebay.com/oauth/api_scope/sell.finances https://api.ebay.com/oauth/api_scope/sell.payment.dispute https://api.ebay.com/oauth/api_scope/commerce.identity.readonly https://api.ebay.com/oauth/api_scope/sell.reputation https://api.ebay.com/oauth/api_scope/sell.reputation.readonly https://api.ebay.com/oauth/api_scope/commerce.notification.subscription https://api.ebay.com/oauth/api_scope/commerce.notification.subscription.readonly https://api.ebay.com/oauth/api_scope/sell.stores https://api.ebay.com/oauth/api_scope/sell.stores.readonly https://api.ebay.com/oauth/scope/sell.edelivery https://api.ebay.com/oauth/api_scope/commerce.vero"

  useEffect(() => {
    setInfo({
      name: "Auth Connections",
      desc: "View and manage integrations with other sites",
    });
  }, []);

  const handleEbayAuth = () => {
    const url = `${ebayBaseUrl}/oauth2/authorize?client_id=${ebayClientId}&response_type=code&redirect_uri=${ebayRuName}&scope=${scope}&state=${customerId}|${userId}|${document.location.origin}/integrations/ebay`;

    console.log("Opening eBay auth URL:", url);

    window.open(url, "_blank");
  };

  const handleTellrooAuth = () => {
    const forwardUrl = `${document.location.origin}/integrations/telleroo`;
    const redirectUrl = encodeURIComponent(
      `${bettyBaseUrl}/v1/integration/telleroo/authorise?c=${customerId}&u=${userId}&forwardUrl=${forwardUrl}`
    );

    const url = `${tellerooBaseUrl}/oauth?client_id=${tellerooClientId}&redirect_uri=${redirectUrl}&response_type=code&scope=create`;

    console.log("Opening Telleroo auth URL:", url);

    window.open(url, "_blank");
  };

  const ebayIntegrationStatus = useMemo(() =>
    integrations.data?.data.find((i) =>
      i.service === IntegrationService.Ebay),
    [integrations.data?.data]);

  const ebayAuthedBy = useMemo(() =>
    !isLoading && ebayIntegrationStatus !== undefined ?
      findUser(ebayIntegrationStatus?.userId)
      :
      undefined,
    [isLoading, ebayIntegrationStatus]);

  const tellerooIntegrationStatus = useMemo(() =>
    integrations.data?.data.find((i) =>
      i.service === IntegrationService.Telleroo),
    [integrations.data?.data]);

  const tellerooAuthedBy = useMemo(() =>
    !isLoading && tellerooIntegrationStatus !== undefined ?
      findUser(tellerooIntegrationStatus.userId)
      :
      undefined,
    [isLoading, tellerooIntegrationStatus]);

  const shopifyIntegrationStatus = useMemo(() =>
    integrations.data?.data.find((i) => 
      i.service === IntegrationService.Shopify),
    [integrations.data?.data]);

  const shopifyAuthedBy = useMemo(() =>
    !isLoading && shopifyIntegrationStatus !== undefined ?
      findUser(shopifyIntegrationStatus.userId)
      :
      undefined,
    [isLoading, shopifyIntegrationStatus]);

  return (
    <div className="bg-white rounded-lg border divide-y">
      {isLoading ?
        <LoadingWheel />
        :
        <>
          <ShopifyAuthModal open={shopifyOpen} setOpen={setShopifyOpen} />
          <div className="py-6 px-4 flex items-center justify-between">
            <p>eBay</p>
            {ebayIntegrationStatus !== undefined ?
              <div className="flex flex-col text-sm">
                <span>{`Expires At: ${date.format(
                  new Date(ebayIntegrationStatus.expiry),
                  "HH:mm:ss DD/MM/YYYY"
                )}`}
                  <span className="text-gray-600 italic">{` (${Math.ceil(
                    (new Date(ebayIntegrationStatus.expiry).getTime() -
                      new Date().getTime()) /
                    (1000 * 60 * 60 * 24)
                  )} days)`}</span>
                </span>
                <span>{`Authorised On: ${date.format(
                  new Date(ebayIntegrationStatus.expiry),
                  "HH:mm:ss DD/MM/YYYY"
                )}`}</span>
                <span>Authorised By: {`${tellerooAuthedBy?.forename} ${tellerooAuthedBy?.surname}`}</span>
              </div>
              :
              <div>
                <span className="text-gray-400 italic text-sm">Not connected</span>
              </div>
            }
            <ButtonInput
              label={"Connect to eBay"}
              isSubmit={false}
              onClick={() => handleEbayAuth()}
              classes={undefined}
            />
          </div>
          <div className="py-6 px-4 flex items-center justify-between">
            <p>Telleroo</p>
            {tellerooIntegrationStatus !== undefined ?
              <div className="flex flex-col text-sm">
                <span>{`Authorised On: ${date.format(
                  new Date(tellerooIntegrationStatus.expiry),
                  "HH:mm:ss DD/MM/YYYY"
                )}`}</span>
                <span>Authorised By: {`${tellerooAuthedBy?.forename} ${tellerooAuthedBy?.surname}`}</span>
              </div>
              :
              <div>
                <span className="text-gray-400 italic text-sm">Not connected</span>
              </div>
            }
            <ButtonInput
              label={"Connect to Telleroo"}
              isSubmit={false}
              onClick={handleTellrooAuth}
              classes={undefined}
            />
          </div>
          <div className="py-6 px-4 flex items-center justify-between">
            <p>Shopify</p>
            {shopifyIntegrationStatus !== undefined ?
              <div className="flex flex-col text-sm">
                <span>{`Authorised On: ${date.format(
                  new Date(shopifyIntegrationStatus.updated ?? shopifyIntegrationStatus.created),
                  "HH:mm:ss DD/MM/YYYY"
                )}`}</span>
                <span>Authorised By: {`${shopifyAuthedBy?.forename} ${shopifyAuthedBy?.surname}`}</span>
              </div>
              :
              <div>
                <span className="text-gray-400 italic text-sm">Not connected</span>
              </div>
            }
            <ButtonInput
              label={"Connect to Shopify"}
              isSubmit={false}
              onClick={() => setShopifyOpen(true)}
              classes={undefined}
              //disabled={shopifyIntegrationStatus !== undefined}
            />
          </div>
        </>
      }
    </div>
  );
}
